import React, { useState, useEffect, useCallback } from "react";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Modal from "@mui/material/Modal";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import dayjs from "dayjs";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import { apiCall } from "../config/api";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  bgcolor: "background.paper",
  border: "2px solid #000",
  boxShadow: 24,
  p: 4,
};

const pinTierOptions = [
  { value: "default", label: "Varsayılan (Süreli)" },
  { value: "permanent_manual", label: "Kalıcı (Manuel)" },
  { value: "free_temporary", label: "Ücretsiz Geçici" },
  { value: "premium_permanent", label: "Premium Kalıcı" },
  // İhtiyaç duyulursa daha fazla seçenek eklenebilir
];

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

const TimeManagementPage = () => {
  const [pins, setPins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [currentPin, setCurrentPin] = useState(null);
  const [editFormData, setEditFormData] = useState({
    visibleFrom: null,
    visibleUntil: null,
    pinTier: "",
  });
  const [selected, setSelected] = useState([]);
  const [filters, setFilters] = useState({
    status: "",
    pinTier: "",
    suspendedStatus: "all",
    searchQuery: "",
    visibleFromRange: "all",
    visibleUntilRange: "all",
  });
  const [filteredPins, setFilteredPins] = useState([]);
  const [openBulkEditModal, setOpenBulkEditModal] = useState(false);
  const [bulkEditFormData, setBulkEditFormData] = useState({
    visibleFrom: null,
    visibleUntil: null,
    pinTier: "",
    makePermanent: false,
    makeInactive: false,
  });
  const [bulkUpdateMessage, setBulkUpdateMessage] = useState({
    type: "",
    text: "",
  });
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("title"); // Default sort by title or ID

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const getPinStatus = useCallback((pin) => {
    const now = dayjs();
    const from = pin.visibleFrom ? dayjs(pin.visibleFrom.seconds * 1000) : null;
    const until = pin.visibleUntil
      ? dayjs(pin.visibleUntil.seconds * 1000)
      : null;

    if (!until) {
      if (from && from.isAfter(now)) {
        return "Zamanlanmış (Kalıcı)";
      }
      return "Kalıcı";
    }
    if (until.isBefore(now)) {
      return "Süresi Dolmuş";
    }
    if (from && from.isAfter(now)) {
      return "Zamanlanmış";
    }
    if (
      (!from || from.isBefore(now) || from.isSame(now)) &&
      until.isAfter(now)
    ) {
      return "Aktif";
    }
    return "Belirsiz"; // Should not happen with proper data
  }, []); // dayjs her zaman aynı instance olduğu için bağımlılık gerekmez

  const getPinSuspendedStatus = useCallback(
    (pin) => {
      const status = getPinStatus(pin);
      if (status === "Aktif" || status === "Kalıcı") {
        return "Evet";
      }
      return "Hayır";
    },
    [getPinStatus]
  ); // dayjs her zaman aynı instance olduğu için bağımlılık gerekmez

  const fetchPins = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiCall("/api/pins", "GET");
      if (!response.success) {
        throw new Error(response.error || "Failed to fetch pins");
      }
      setPins(response.pins || []);
      setError(null);
    } catch (err) {
      console.error("Error fetching pins: ", err);
      setError("Pinler yüklenirken bir hata oluştu.");
      setPins([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = filteredPins.map((n) => n.id);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleRowCheckboxClick = (event, id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }
    setSelected(newSelected);
  };

  const isSelected = (id) => selected.indexOf(id) !== -1;

  const handleFilterChange = (eventOrDate, name, isDate = false) => {
    if (isDate) {
      setFilters((prev) => ({ ...prev, [name]: eventOrDate })); // eventOrDate is the date object itself
    } else {
      const { name: inputName, value } = eventOrDate.target;
      setFilters((prev) => ({ ...prev, [inputName]: value }));
    }
  };

  const handleBulkSave = async () => {
    if (selected.length === 0) {
      setBulkUpdateMessage({
        type: "error",
        text: "Güncellenecek pin seçilmedi.",
      });
      return;
    }

    setLoading(true);
    setBulkUpdateMessage({ type: "", text: "" });
    const dataToUpdate = {};

    if (bulkEditFormData.makeInactive) {
      dataToUpdate.visibleFrom = dayjs().subtract(2, "days").toISOString();
      dataToUpdate.visibleUntil = dayjs().subtract(1, "day").toISOString();
    } else if (bulkEditFormData.makePermanent) {
      dataToUpdate.visibleUntil = null;
      if (bulkEditFormData.visibleFrom) {
        dataToUpdate.visibleFrom = dayjs(
          bulkEditFormData.visibleFrom
        ).toISOString();
      }
    } else {
      if (bulkEditFormData.visibleFrom) {
        dataToUpdate.visibleFrom = dayjs(
          bulkEditFormData.visibleFrom
        ).toISOString();
      }
      if (bulkEditFormData.visibleUntil) {
        dataToUpdate.visibleUntil = dayjs(
          bulkEditFormData.visibleUntil
        ).toISOString();
      } else if (
        bulkEditFormData.visibleFrom !== undefined &&
        bulkEditFormData.visibleUntil === null
      ) {
        dataToUpdate.visibleUntil = null;
      }
    }

    if (bulkEditFormData.pinTier) {
      dataToUpdate.pinTier = bulkEditFormData.pinTier;
    }

    if (Object.keys(dataToUpdate).length === 0) {
      setBulkUpdateMessage({
        type: "info",
        text: "Güncellenecek bir değişiklik yapılmadı.",
      });
      setLoading(false);
      setOpenBulkEditModal(false);
      setBulkEditFormData({
        visibleFrom: null,
        visibleUntil: null,
        pinTier: "",
        makePermanent: false,
      });
      return;
    }

    try {
      const response = await apiCall("/api/pins/bulk", "PATCH", {
        pinIds: selected,
        updateData: dataToUpdate,
      });

      if (!response.success) {
        throw new Error(response.error || "Bulk update failed");
      }

      setBulkUpdateMessage({
        type: "success",
        text: `${selected.length} pin başarıyla güncellendi.`,
      });
      fetchPins(); // Veriyi yenile
      setSelected([]); // Seçimi temizle
    } catch (error) {
      console.error("Error bulk updating pins: ", error);
      setBulkUpdateMessage({
        type: "error",
        text: "Pinler güncellenirken bir hata oluştu: " + error.message,
      });
    } finally {
      setLoading(false);
      setOpenBulkEditModal(false);
      setBulkEditFormData({
        visibleFrom: null,
        visibleUntil: null,
        pinTier: "",
        makePermanent: false,
      }); // Formu sıfırla
    }
  };

  const dateFilterOptions = [
    { value: "all", label: "Tümü" },
    { value: "today", label: "Bugün" },
    { value: "tomorrow", label: "Yarın" },
    { value: "yesterday", label: "Dün" },
    { value: "this_week", label: "Bu Hafta" },
    { value: "next_week", label: "Gelecek Hafta" },
    { value: "last_week", label: "Geçen Hafta" },
    { value: "this_month", label: "Bu Ay" },
    { value: "next_month", label: "Gelecek Ay" },
    { value: "last_month", label: "Geçen Ay" },
  ];

  const clearFilters = () => {
    setFilters({
      status: "",
      pinTier: "",
      suspendedStatus: "all",
      searchQuery: "",
      visibleFromRange: "all",
      visibleUntilRange: "all",
    });
  };

  useEffect(() => {
    let tempPins = [...pins];

    // Search Query Filter (if exists in your filters state)
    if (filters.searchQuery) {
      const lowercasedQuery = filters.searchQuery.toLowerCase();
      tempPins = tempPins.filter(
        (pin) =>
          (pin.title && pin.title.toLowerCase().includes(lowercasedQuery)) ||
          (pin.id && pin.id.toLowerCase().includes(lowercasedQuery))
        // Add other fields to search if necessary
      );
    }

    // Status Filter
    if (filters.status) {
      tempPins = tempPins.filter((pin) => getPinStatus(pin) === filters.status);
    }

    // Suspended Status Filter
    if (filters.suspendedStatus !== "all") {
      tempPins = tempPins.filter(
        (pin) =>
          getPinSuspendedStatus(pin) ===
          (filters.suspendedStatus === "yes" ? "Evet" : "Hayır")
      );
    }

    // Pin Tier Filter
    if (filters.pinTier) {
      tempPins = tempPins.filter(
        (pin) => (pin.pinTier || "default") === filters.pinTier
      );
    }

    // Date Range Filters with Select
    const now = dayjs();
    const applyDateRangeFilter = (pinDate, rangeKey) => {
      if (!pinDate) return false;
      const dateToFilter = dayjs(pinDate.seconds * 1000);
      switch (rangeKey) {
        case "today":
          return dateToFilter.isSame(now, "day");
        case "tomorrow":
          return dateToFilter.isSame(now.add(1, "day"), "day");
        case "yesterday":
          return dateToFilter.isSame(now.subtract(1, "day"), "day");
        case "this_week":
          return dateToFilter.isSame(now, "week");
        case "next_week":
          return dateToFilter.isSame(now.add(1, "week"), "week");
        case "last_week":
          return dateToFilter.isSame(now.subtract(1, "week"), "week");
        case "this_month":
          return dateToFilter.isSame(now, "month");
        case "next_month":
          return dateToFilter.isSame(now.add(1, "month"), "month");
        case "last_month":
          return dateToFilter.isSame(now.subtract(1, "month"), "month");
        default:
          return true; // 'all' or unknown
      }
    };

    if (filters.visibleFromRange !== "all") {
      tempPins = tempPins.filter((pin) =>
        applyDateRangeFilter(pin.visibleFrom, filters.visibleFromRange)
      );
    }
    if (filters.visibleUntilRange !== "all") {
      tempPins = tempPins.filter((pin) =>
        applyDateRangeFilter(pin.visibleUntil, filters.visibleUntilRange)
      );
    }

    setFilteredPins(tempPins);
    // Reset selections when filters change
    setSelected([]);
    setBulkUpdateMessage({ type: "", text: "" });
  }, [pins, filters, getPinStatus, getPinSuspendedStatus]);

  useEffect(() => {
    fetchPins();
  }, [fetchPins]);

  // filteredPins'i ilk yüklemede ve pinler değiştiğinde ayarla
  useEffect(() => {
    setFilteredPins(pins);
  }, [pins]);

  const handleEditClick = (pin) => {
    setCurrentPin(pin);
    setEditFormData({
      visibleFrom: pin.visibleFrom
        ? dayjs(pin.visibleFrom.seconds * 1000)
        : null,
      visibleUntil: pin.visibleUntil
        ? dayjs(pin.visibleUntil.seconds * 1000)
        : null,
      pinTier: pin.pinTier || "default",
    });
    setOpenEditModal(true);
  };

  const handleCloseEditModal = () => {
    setOpenEditModal(false);
    setCurrentPin(null);
    setEditFormData({ visibleFrom: null, visibleUntil: null, pinTier: "" });
  };

  const handleDateTimeChange = (name, newValue) => {
    setEditFormData((prev) => ({ ...prev, [name]: newValue }));
  };

  const handleTierChange = (event) => {
    setEditFormData((prev) => ({ ...prev, pinTier: event.target.value }));
  };

  const handleSaveChanges = async () => {
    if (!currentPin) return;
    setLoading(true);
    try {
      const dataToUpdate = {
        pinTier: editFormData.pinTier,
        visibleFrom: editFormData.visibleFrom
          ? editFormData.visibleFrom.toISOString()
          : null,
        visibleUntil: editFormData.visibleUntil
          ? editFormData.visibleUntil.toISOString()
          : null,
      };

      const response = await apiCall(
        `/api/pins/${currentPin.id}`,
        "PATCH",
        dataToUpdate
      );

      if (!response.success) {
        throw new Error(response.error || "Update failed");
      }

      handleCloseEditModal();
      fetchPins();
      alert("Pin başarıyla güncellendi!");
    } catch (err) {
      console.error("Error updating pin: ", err);
      setError("Pin güncellenirken bir hata oluştu: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Pin Zaman Yönetimi
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          Filtreler
        </Typography>
        <Box
          sx={{
            display: "flex",
            gap: 1.5,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="status-filter-label">
              Duruma Göre Filtrele
            </InputLabel>
            <Select
              labelId="status-filter-label"
              name="status"
              value={filters.status}
              label="Duruma Göre Filtrele"
              variant="outlined"
              onChange={handleFilterChange}
            >
              <MenuItem value="">
                <em>Tümü</em>
              </MenuItem>
              <MenuItem value="Aktif">Aktif</MenuItem>
              <MenuItem value="Kalıcı">Kalıcı</MenuItem>
              <MenuItem value="Zamanlanmış">Zamanlanmış</MenuItem>
              <MenuItem value="Zamanlanmış (Kalıcı)">
                Zamanlanmış (Kalıcı)
              </MenuItem>
              <MenuItem value="Süresi Dolmuş">Süresi Dolmuş</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="tier-filter-label">
              Seviyeye Göre Filtrele
            </InputLabel>
            <Select
              labelId="tier-filter-label"
              name="pinTier"
              value={filters.pinTier}
              label="Seviyeye Göre Filtrele"
              variant="outlined"
              onChange={handleFilterChange}
            >
              <MenuItem value="">
                <em>Tümü</em>
              </MenuItem>
              {pinTierOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="suspended-status-filter-label">
              Askıda mı?
            </InputLabel>
            <Select
              labelId="suspended-status-filter-label"
              name="suspendedStatus" // Make sure this matches the state property
              value={filters.suspendedStatus}
              label="Askıda mı?"
              onChange={handleFilterChange}
            >
              <MenuItem value="all">
                <em>Tümü</em>
              </MenuItem>
              <MenuItem value="yes">Evet</MenuItem>
              <MenuItem value="no">Hayır</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="visible-from-range-label">
              Gör. Baş. Aralığı
            </InputLabel>
            <Select
              labelId="visible-from-range-label"
              name="visibleFromRange"
              value={filters.visibleFromRange}
              label="Gör. Baş. Aralığı"
              onChange={handleFilterChange}
            >
              {dateFilterOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="visible-until-range-label">
              Gör. Bitiş Aralığı
            </InputLabel>
            <Select
              labelId="visible-until-range-label"
              name="visibleUntilRange"
              value={filters.visibleUntilRange}
              label="Gör. Bitiş Aralığı"
              onChange={handleFilterChange}
            >
              {dateFilterOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            size="small"
            onClick={clearFilters}
            sx={{ height: "fit-content", alignSelf: "center" }}
          >
            Filtreleri Temizle
          </Button>
        </Box>
      </Paper>

      <Box
        sx={{
          mb: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="subtitle1">
          {selected.length > 0
            ? `${selected.length} pin seçili`
            : "Pin seçilmedi"}
        </Typography>
        <Button
          variant="contained"
          onClick={() => setOpenBulkEditModal(true)}
          disabled={selected.length === 0}
        >
          Seçilenleri Toplu Düzenle
        </Button>
      </Box>
      {/* Bulk update message and loading indicator */}
      {bulkUpdateMessage.text && (
        <Alert
          severity={bulkUpdateMessage.type || "info"}
          sx={{ my: 2 }}
          onClose={() => setBulkUpdateMessage({ type: "", text: "" })}
        >
          {bulkUpdateMessage.text}
        </Alert>
      )}
      {loading && (
        <CircularProgress sx={{ display: "block", margin: "auto", my: 2 }} />
      )}
      {error && (
        <Alert severity="error" sx={{ my: 2 }}>
          {error}
        </Alert>
      )}

      {/* Bulk Edit Modal */}
      <Modal
        open={openBulkEditModal}
        onClose={() => {
          setOpenBulkEditModal(false);
          setBulkEditFormData({
            visibleFrom: null,
            visibleUntil: null,
            pinTier: "",
            makePermanent: false,
            makeInactive: false,
          });
        }}
        aria-labelledby="bulk-edit-modal-title"
      >
        <Box sx={modalStyle}>
          <Typography
            id="bulk-edit-modal-title"
            variant="h6"
            component="h2"
            gutterBottom
          >
            Seçili Pinleri Toplu Düzenle ({selected.length} pin)
          </Typography>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DateTimePicker
              label="Görünür Başlangıç Tarihi (Değiştirilecekse seçin)"
              value={bulkEditFormData.visibleFrom}
              onChange={(newValue) =>
                setBulkEditFormData((prev) => ({
                  ...prev,
                  visibleFrom: newValue,
                }))
              }
              renderInput={(params) => (
                <TextField {...params} fullWidth margin="normal" />
              )}
              ampm={false}
              disabled={
                bulkEditFormData.makePermanent || bulkEditFormData.makeInactive
              }
            />
            <DateTimePicker
              label="Görünür Bitiş Tarihi (Değiştirilecekse seçin)"
              value={bulkEditFormData.visibleUntil}
              onChange={(newValue) =>
                setBulkEditFormData((prev) => ({
                  ...prev,
                  visibleUntil: newValue,
                }))
              }
              renderInput={(params) => (
                <TextField {...params} fullWidth margin="normal" />
              )}
              ampm={false}
              disabled={
                bulkEditFormData.makePermanent || bulkEditFormData.makeInactive
              }
            />
          </LocalizationProvider>
          <FormControlLabel
            control={
              <Checkbox
                checked={bulkEditFormData.makePermanent}
                onChange={(e) => {
                  setBulkEditFormData((prev) => ({
                    ...prev,
                    makePermanent: e.target.checked,
                    visibleFrom: e.target.checked ? null : prev.visibleFrom,
                    visibleUntil: e.target.checked ? null : prev.visibleUntil,
                  }));
                }}
              />
            }
            label="Kalıcı Yap (Bitiş tarihini kaldırır)"
            sx={{ mt: 1 }}
            disabled={bulkEditFormData.makeInactive} // Disable if makeInactive is checked
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={bulkEditFormData.makeInactive}
                onChange={(e) => {
                  setBulkEditFormData((prev) => ({
                    ...prev,
                    makeInactive: e.target.checked,
                    // If making inactive, also uncheck makePermanent and clear dates
                    makePermanent: e.target.checked
                      ? false
                      : prev.makePermanent,
                    visibleFrom: e.target.checked ? null : prev.visibleFrom,
                    visibleUntil: e.target.checked ? null : prev.visibleUntil,
                  }));
                }}
              />
            }
            label="Pasif Yap (Pinleri geçmiş tarihli yapar)"
            sx={{ mt: 1 }}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel id="bulk-pin-tier-label">
              Pin Seviyesi (Değiştirilecekse seçin)
            </InputLabel>
            <Select
              labelId="bulk-pin-tier-label"
              value={bulkEditFormData.pinTier}
              label="Pin Seviyesi (Değiştirilecekse seçin)"
              onChange={(e) =>
                setBulkEditFormData((prev) => ({
                  ...prev,
                  pinTier: e.target.value,
                }))
              }
            >
              <MenuItem value="">
                <em>Değişiklik Yok</em>
              </MenuItem>
              {pinTierOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box
            sx={{ mt: 3, display: "flex", justifyContent: "flex-end", gap: 1 }}
          >
            <Button
              variant="outlined"
              onClick={() => {
                setOpenBulkEditModal(false);
                setBulkEditFormData({
                  visibleFrom: null,
                  visibleUntil: null,
                  pinTier: "",
                  makePermanent: false,
                  makeInactive: false,
                });
              }}
            >
              İptal
            </Button>
            <Button variant="contained" onClick={handleBulkSave}>
              Kaydet
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Table Container */}
      <TableContainer component={Paper} sx={{ mt: 2, maxHeight: "70vh" }}>
        <Table stickyHeader aria-label="sticky table">
          <TableHead>
            <TableRow>
              <TableCell
                padding="checkbox"
                sx={{ backgroundColor: "background.paper", zIndex: 1101 }}
              >
                <Checkbox
                  color="primary"
                  indeterminate={
                    selected.length > 0 && selected.length < filteredPins.length
                  }
                  checked={
                    filteredPins.length > 0 &&
                    selected.length === filteredPins.length
                  }
                  onChange={handleSelectAllClick}
                  inputProps={{ "aria-label": "select all pins" }}
                />
              </TableCell>
              <TableCell
                sortDirection={orderBy === "title" ? order : false}
                sx={{ backgroundColor: "background.paper", zIndex: 1101 }}
              >
                <TableSortLabel
                  active={orderBy === "title"}
                  direction={orderBy === "title" ? order : "asc"}
                  onClick={(event) => handleRequestSort(event, "title")}
                >
                  Başlık
                </TableSortLabel>
              </TableCell>
              <TableCell
                sx={{ backgroundColor: "background.paper", zIndex: 1101 }}
              >
                Pin ID
              </TableCell>
              <TableCell
                align="right"
                sortDirection={orderBy === "visibleFrom" ? order : false}
                sx={{ backgroundColor: "background.paper", zIndex: 1101 }}
              >
                <TableSortLabel
                  active={orderBy === "visibleFrom"}
                  direction={orderBy === "visibleFrom" ? order : "asc"}
                  onClick={(event) => handleRequestSort(event, "visibleFrom")}
                >
                  Görünür Başlangıç
                </TableSortLabel>
              </TableCell>
              <TableCell
                align="right"
                sortDirection={orderBy === "visibleUntil" ? order : false}
                sx={{ backgroundColor: "background.paper", zIndex: 1101 }}
              >
                <TableSortLabel
                  active={orderBy === "visibleUntil"}
                  direction={orderBy === "visibleUntil" ? order : "asc"}
                  onClick={(event) => handleRequestSort(event, "visibleUntil")}
                >
                  Görünür Bitiş
                </TableSortLabel>
              </TableCell>
              <TableCell
                sx={{ backgroundColor: "background.paper", zIndex: 1101 }}
              >
                Seviye
              </TableCell>
              <TableCell
                sx={{ backgroundColor: "background.paper", zIndex: 1101 }}
              >
                Durum
              </TableCell>
              <TableCell
                sx={{ backgroundColor: "background.paper", zIndex: 1101 }}
              >
                Aktif mi?
              </TableCell>
              <TableCell
                align="right"
                sortDirection={orderBy === "createdAt" ? order : false}
                sx={{ backgroundColor: "background.paper", zIndex: 1101 }}
              >
                <TableSortLabel
                  active={orderBy === "createdAt"}
                  direction={orderBy === "createdAt" ? order : "asc"}
                  onClick={(event) => handleRequestSort(event, "createdAt")}
                >
                  Oluşturulma Tarihi
                </TableSortLabel>
              </TableCell>
              <TableCell
                align="center"
                sx={{ backgroundColor: "background.paper", zIndex: 1101 }}
              >
                Eylemler
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredPins.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  Gösterilecek pin bulunamadı.
                </TableCell>
              </TableRow>
            ) : (
              stableSort(filteredPins, getComparator(order, orderBy)).map(
                (pin) => (
                  <TableRow
                    hover
                    onClick={(event) => handleRowCheckboxClick(event, pin.id)}
                    role="checkbox"
                    aria-checked={isSelected(pin.id)}
                    tabIndex={-1}
                    key={pin.id}
                    selected={isSelected(pin.id)}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        color="primary"
                        checked={isSelected(pin.id)}
                        inputProps={{
                          "aria-labelledby": `pin-checkbox-${pin.id}`,
                        }}
                      />
                    </TableCell>
                    <TableCell
                      component="th"
                      id={`pin-checkbox-${pin.id}`}
                      scope="row"
                    >
                      {pin.title || "Başlık Yok"}
                    </TableCell>
                    <TableCell>{pin.id}</TableCell>
                    <TableCell align="right">
                      {pin.visibleFrom
                        ? dayjs(pin.visibleFrom.seconds * 1000).format(
                            "DD/MM/YYYY HH:mm"
                          )
                        : "Belirtilmemiş"}
                    </TableCell>
                    <TableCell align="right">
                      {pin.visibleUntil
                        ? dayjs(pin.visibleUntil.seconds * 1000).format(
                            "DD/MM/YYYY HH:mm"
                          )
                        : "Kalıcı"}
                    </TableCell>
                    <TableCell>{pin.pinTier || "N/A"}</TableCell>
                    <TableCell>{getPinStatus(pin)}</TableCell>
                    <TableCell>{getPinSuspendedStatus(pin)}</TableCell>
                    <TableCell align="right">
                      {pin.createdAt
                        ? dayjs(pin.createdAt.seconds * 1000).format(
                            "DD/MM/YYYY HH:mm"
                          )
                        : "N/A"}
                    </TableCell>
                    <TableCell align="center">
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditClick(pin);
                        }}
                      >
                        Düzenle
                      </Button>
                    </TableCell>
                  </TableRow>
                )
              )
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Single Edit Modal (Dialog) */}
      <Dialog open={openEditModal} onClose={handleCloseEditModal}>
        <DialogTitle>Pin Zamanlamasını Düzenle</DialogTitle>
        <DialogContent>
          {currentPin && (
            <Box component="form" noValidate autoComplete="off" sx={{ pt: 1 }}>
              <Typography variant="subtitle1" gutterBottom>
                Pin: {currentPin.title || currentPin.id}
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DateTimePicker
                  label="Görünür Başlangıç Tarihi"
                  value={editFormData.visibleFrom}
                  onChange={(newValue) =>
                    handleDateTimeChange("visibleFrom", newValue)
                  }
                  renderInput={(params) => (
                    <TextField {...params} fullWidth margin="normal" />
                  )}
                  ampm={false}
                />
                <DateTimePicker
                  label="Görünür Bitiş Tarihi (Kalıcı için boş bırakın)"
                  value={editFormData.visibleUntil}
                  onChange={(newValue) =>
                    handleDateTimeChange("visibleUntil", newValue)
                  }
                  renderInput={(params) => (
                    <TextField {...params} fullWidth margin="normal" />
                  )}
                  ampm={false}
                />
              </LocalizationProvider>
              <FormControl fullWidth margin="normal">
                <InputLabel id="pin-tier-label-single">Pin Seviyesi</InputLabel>
                <Select
                  labelId="pin-tier-label-single"
                  value={editFormData.pinTier}
                  label="Pin Seviyesi"
                  onChange={handleTierChange}
                >
                  {pinTierOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditModal}>İptal</Button>
          <Button onClick={handleSaveChanges} variant="contained">
            Kaydet
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TimeManagementPage;
